
import { getCommonPrintStyles, getHeaderHtml, getFooterHtml, getSignatureHtml } from "./printStyles";
import { downloadAsJPG, downloadAsPDF, createPrintDocument } from "./printingBridge";
import { toast } from "sonner";

const STUDENTS_PER_PAGE = 12; // Changed from 18 to 12 as requested

interface StudentData {
  id: string | number;
  name: string;
  surname?: string;
  fathersName: string;
  programCategory: string;
  programTitle: string;
  fees: number;
  paymentStatus: string;
  remainingDays: number;
}

/**
 * Export students list as JPG with pagination support - Ultra-fast download
 */
export const printStudentsListPaginatedAsJPG = async (
  students: StudentData[],
  startPage: number = 1,
  endPage: number = 1
) => {
  try {
    const loadingToast = toast.loading('Generating students list JPG...');
    
    const { content, filename } = generateStudentListContent(students, startPage, endPage);
    
    // Download as JPG with ultra-fast settings
    await downloadAsJPG(content, filename, {
      onError: (error) => {
        console.error('JPG download error:', error);
        toast.dismiss(loadingToast);
        toast.error('Failed to download students list');
      }
    });
    
    toast.dismiss(loadingToast);
    const pageText = startPage === endPage ? `page ${startPage}` : `pages ${startPage}-${endPage}`;
    toast.success(`Students list ${pageText} downloaded as JPG!`);
    
  } catch (error) {
    console.error('Error downloading paginated students list as JPG:', error);
    toast.error('Failed to download students list');
  }
};

/**
 * Export students list as PDF with pagination support - Ultra-fast download
 */
export const printStudentsListPaginatedAsPDF = async (
  students: StudentData[],
  startPage: number = 1,
  endPage: number = 1
) => {
  try {
    const loadingToast = toast.loading('Generating students list PDF...');
    
    const { content, filename } = generateStudentListContent(students, startPage, endPage);
    
    // Download as PDF with ultra-fast settings
    await downloadAsPDF(content, filename, {
      onError: (error) => {
        console.error('PDF download error:', error);
        toast.dismiss(loadingToast);
        toast.error('Failed to download students list');
      }
    });
    
    toast.dismiss(loadingToast);
    const pageText = startPage === endPage ? `page ${startPage}` : `pages ${startPage}-${endPage}`;
    toast.success(`Students list ${pageText} downloaded as PDF!`);
    
  } catch (error) {
    console.error('Error downloading paginated students list as PDF:', error);
    toast.error('Failed to download students list');
  }
};

/**
 * Generate common content for both JPG and PDF downloads
 */
const generateStudentListContent = (
  students: StudentData[],
  startPage: number,
  endPage: number
) => {
  const totalPages = Math.ceil(students.length / STUDENTS_PER_PAGE);
  const actualEndPage = Math.min(endPage, totalPages);
  const actualStartPage = Math.max(1, startPage);
  
  // Calculate which students to include
  const startIndex = (actualStartPage - 1) * STUDENTS_PER_PAGE;
  const endIndex = actualEndPage * STUDENTS_PER_PAGE;
  const studentsToShow = students.slice(startIndex, endIndex);
  
  // Group students by pages
  const pages: StudentData[][] = [];
  for (let i = 0; i < studentsToShow.length; i += STUDENTS_PER_PAGE) {
    pages.push(studentsToShow.slice(i, i + STUDENTS_PER_PAGE));
  }
  
  // Generate content for all pages using standardized structure
  const allPagesContent = pages.map((pageStudents, pageIndex) => {
    const currentPageNumber = actualStartPage + pageIndex;
    
    return `
      <div class="print-page ${pageIndex > 0 ? 'page-break' : ''}">
        ${getHeaderHtml(`Students List - Page ${currentPageNumber} of ${totalPages}`)}
        
        <div class="print-content">
          <div class="page-info">
            <strong>Page ${currentPageNumber} of ${totalPages}</strong> | 
            Students ${(currentPageNumber - 1) * STUDENTS_PER_PAGE + 1} - ${Math.min(currentPageNumber * STUDENTS_PER_PAGE, students.length)} of ${students.length}
          </div>
          
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Student</th>
                <th>Father's Name</th>
                <th>Program Category</th>
                <th>Program</th>
                <th>Fees (AFN)</th>
                <th>Payment Status</th>
                <th>Remaining Days</th>
              </tr>
            </thead>
            <tbody>
              ${pageStudents.map((student) => `
                <tr>
                  <td>${student.id}</td>
                  <td>${student.name}${student.surname ? ' ' + student.surname : ''}</td>
                  <td>${student.fathersName}</td>
                  <td>${student.programCategory}</td>
                  <td>${student.programTitle}</td>
                  <td>AFN ${student.fees}</td>
                  <td class="status-${student.paymentStatus}">${student.paymentStatus}</td>
                  <td>${student.remainingDays} days</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          
          ${getSignatureHtml('student')}
        </div>
        
        ${getFooterHtml('student')}
      </div>
    `;
  }).join('');

  const styles = `
    ${getCommonPrintStyles()}
    .page-info {
      text-align: center;
      font-size: 0.9em;
      color: #666;
      margin: 10px 0;
      padding: 8px;
      background: #f8f9fa;
      border-radius: 4px;
    }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 16px;
    }
    th, td {
      padding: 8px 6px;
      border-bottom: 1px solid #ddd;
      text-align: left;
      font-size: 0.85em;
      vertical-align: top;
    }
    th {
      background: #f5f5f5;
      font-weight: bold;
      border-bottom: 2px solid #ccc;
    }
    .status-paid {
      color: #2e7d32;
      font-weight: 500;
    }
    .status-partial {
      color: #ed6c02;
      font-weight: 500;
    }
    .status-pending {
      color: #d32f2f;
      font-weight: 500;
    }
  `;

  // Generate the complete print document
  const content = createPrintDocument(allPagesContent, 'Students_List_Paginated', styles);
  
  // Create filename
  const filename = actualStartPage === actualEndPage 
    ? `Students_List_Page_${actualStartPage}_${new Date().toISOString().split('T')[0]}`
    : `Students_List_Pages_${actualStartPage}-${actualEndPage}_${new Date().toISOString().split('T')[0]}`;

  return { content, filename };
};
